import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { AdminService, AdminCampaign } from '../../services/adminService';
import { getCampaign, Campaign } from '../../services/campaignService';
import { Timestamp } from 'firebase/firestore';
import { usePageTitle } from '../../hooks/usePageTitle';

interface AdminCampaignsPageProps {
}

const AdminCampaignsPage: React.FC<AdminCampaignsPageProps> = () => {
  const [searchTerm, setSearchTerm] = useState('');
  usePageTitle('Admin Campaigns');
  const { isDark } = useAdminTheme();
  const { showError } = useNotification();
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // local searchTerm removed
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('AdminCampaignsPage: Loading campaigns...');
      const campaignsData = await AdminService.getAllCampaigns();
      console.log('AdminCampaignsPage: Loaded campaigns:', campaignsData.length);
      setCampaigns(campaignsData);

      if (campaignsData.length === 0) {
        console.log('AdminCampaignsPage: No campaigns found in database');
        setError(null); // Not an error, just no data
      }
    } catch (error) {
      console.error('AdminCampaignsPage: Error loading campaigns:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(errorMessage);
      showError(`Failed to load campaigns: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'sent':
        return isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800';
      case 'scheduled':
        return isDark ? 'bg-blue-900/20 text-blue-300' : 'bg-blue-100 text-blue-800';
      case 'sending':
        return isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
      case 'paused':
        return isDark ? 'bg-orange-900/20 text-orange-300' : 'bg-orange-100 text-orange-800';
      default:
        return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    // Only show sent campaigns (filter out drafts, scheduled, etc.)
    if (campaign.status !== 'sent') return false;

    // Apply search filter if search term exists
    const search = searchTerm.trim();
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      campaign.name.toLowerCase().includes(searchLower) ||
      campaign.userEmail.toLowerCase().includes(searchLower) ||
      campaign.id.toLowerCase().includes(searchLower) ||
      campaign.status.toLowerCase().includes(searchLower) ||
      (campaign.workspaceId && campaign.workspaceId.toLowerCase().includes(searchLower))
    );
  });

  const handleViewCampaign = async (campaign: AdminCampaign) => {
    if (!campaign.id) {
      showError('Campaign ID is missing');
      return;
    }

    try {
      setModalLoading(true);
      setShowModal(true);

      // Fetch full campaign details
      const campaignData = await getCampaign(campaign.id);
      if (campaignData) {
        setSelectedCampaign(campaignData);
      } else {
        showError('Campaign not found');
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error loading campaign details:', error);
      showError('Failed to load campaign details');
      setShowModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCampaign(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <div>
          <h2 className="text-2xl font-bold">Sent Campaigns</h2>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Monitor and manage all sent campaigns across the platform
          </p>
        </div>

        <div className="flex items-center space-x-4 w-full md:w-auto">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Filter campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`block w-full pl-10 pr-3 py-2 border rounded-lg leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm transition-all duration-200 ${isDark
                ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 shadow-sm'
                }`}
            />
          </div>
          <button
            onClick={loadCampaigns}
            disabled={loading}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${isDark
              ? 'bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-50'
              : 'bg-gray-100 hover:bg-gray-200 text-gray-900 disabled:opacity-50'
              }`}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <div className="text-sm font-medium whitespace-nowrap">
            Total: {campaigns.length} campaigns
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`rounded-lg p-4 border ${isDark ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium">Error loading campaigns</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar Removed (Now using global search in header) */}

      {/* Campaigns Table */}
      <div className={`rounded-lg shadow overflow-hidden ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  Campaign Name
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  User Email
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  Created Date
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  Recipients
                </th>
                <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {filteredCampaigns.length > 0 ? (
                filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-6 py-4 whitespace-nowrap ${isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                      <div className="text-sm font-medium">{campaign.name || 'Untitled Campaign'}</div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ID: {campaign.id}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                      {campaign.userEmail || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(campaign.status)}`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                      {formatDate(campaign.createdAt)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                      {campaign.recipientCount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleViewCampaign(campaign)}
                        className={`transition-colors duration-200 ${isDark
                          ? 'text-cyan-400 hover:text-cyan-300'
                          : 'text-cyan-600 hover:text-cyan-900'
                          }`}
                      >
                        View Campaign
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-500' : 'text-gray-400'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                        {searchTerm ? 'No campaigns found' : 'No campaigns available'}
                      </h3>
                      {searchTerm && (
                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          No sent campaigns match "{searchTerm}"
                        </p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Details Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div
            className={`rounded-lg shadow-xl w-[95vw] max-w-[95vw] max-h-[95vh] overflow-hidden flex flex-col ${isDark ? 'bg-gray-800' : 'bg-white'
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Campaign Details
              </h3>
              <button
                onClick={closeModal}
                className={`p-2 rounded-lg transition-colors duration-200 ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-500'
                  }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {modalLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
                </div>
              ) : selectedCampaign ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Campaign Details */}
                  <div className="space-y-6">
                    {/* Campaign Name and Status */}
                    <div>
                      <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCampaign.name || 'Untitled Campaign'}
                      </h4>
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeColor(selectedCampaign.status)}`}>
                          {selectedCampaign.status.charAt(0).toUpperCase() + selectedCampaign.status.slice(1)}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Created: {formatDate(selectedCampaign.createdAt)}
                        </span>
                        {selectedCampaign.sentAt && (
                          <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Sent: {formatDate(selectedCampaign.sentAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Campaign Information Grid */}
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} p-4 rounded-lg`}>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Subject</label>
                        <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCampaign.subject || 'N/A'}</p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Sender Name</label>
                        <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCampaign.senderName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Sender Email</label>
                        <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCampaign.senderEmail || 'N/A'}</p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Reply To</label>
                        <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedCampaign.replyToEmail || selectedCampaign.senderEmail || 'N/A'}</p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Recipient Type</label>
                        <p className={`mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {selectedCampaign.recipientType ? selectedCampaign.recipientType.charAt(0).toUpperCase() + selectedCampaign.recipientType.slice(1) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Campaign ID</label>
                        <p className={`mt-1 text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{selectedCampaign.id || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Statistics */}
                    {selectedCampaign.statistics && (
                      <div>
                        <h5 className={`text-md font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Statistics</h5>
                        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} p-4 rounded-lg`}>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Recipients</label>
                            <p className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {selectedCampaign.statistics.totalRecipients || 0}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sent</label>
                            <p className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {selectedCampaign.statistics.sent || 0}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Opened</label>
                            <p className={`text-lg font-bold mt-1 text-green-600`}>
                              {selectedCampaign.statistics.opened || 0}
                            </p>
                            {selectedCampaign.statistics.sent > 0 && (
                              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {((selectedCampaign.statistics.opened / selectedCampaign.statistics.sent) * 100).toFixed(1)}%
                              </p>
                            )}
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Clicked</label>
                            <p className={`text-lg font-bold mt-1 text-blue-600`}>
                              {selectedCampaign.statistics.clicked || 0}
                            </p>
                            {selectedCampaign.statistics.sent > 0 && (
                              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {((selectedCampaign.statistics.clicked / selectedCampaign.statistics.sent) * 100).toFixed(1)}%
                              </p>
                            )}
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Delivered</label>
                            <p className={`text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {selectedCampaign.statistics.delivered || 0}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Bounced</label>
                            <p className={`text-lg font-bold mt-1 text-red-600`}>
                              {selectedCampaign.statistics.bounced || 0}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Complained</label>
                            <p className={`text-lg font-bold mt-1 text-orange-600`}>
                              {selectedCampaign.statistics.complained || 0}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Unsubscribed</label>
                            <p className={`text-lg font-bold mt-1 text-purple-600`}>
                              {selectedCampaign.statistics.unsubscribed || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Preview Text */}
                    {selectedCampaign.previewText && (
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Preview Text</label>
                        <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{selectedCampaign.previewText}</p>
                      </div>
                    )}

                    {/* Tracking Settings */}
                    <div>
                      <h5 className={`text-md font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Tracking Settings</h5>
                      <div className="flex space-x-4">
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Open Tracking: {selectedCampaign.openTracking ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Click Tracking: {selectedCampaign.clickTracking ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Google Analytics: {selectedCampaign.googleAnalyticsTracking ? '✓ Enabled' : '✗ Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Email Preview */}
                  <div className="space-y-4 sticky top-0">
                    <div>
                      <h5 className={`text-md font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Design Preview</h5>
                      <div className={`rounded-lg shadow-lg overflow-hidden border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                        {/* Email Preview Header */}
                        <div className={`px-4 py-3 border-b ${isDark ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                              <span className={`ml-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Email Preview</span>
                            </div>
                          </div>
                          <div className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <div><strong>To:</strong> [Recipients]</div>
                            <div><strong>From:</strong> {selectedCampaign.senderName} &lt;{selectedCampaign.senderEmail}&gt;</div>
                            <div><strong>Subject:</strong> {selectedCampaign.subject || 'N/A'}</div>
                          </div>
                        </div>

                        {/* Email Content Preview */}
                        <div className="p-4">
                          {selectedCampaign.htmlContent ? (
                            <div className="border rounded-lg overflow-hidden bg-white" style={{ maxHeight: 'calc(95vh - 300px)', overflowY: 'auto' }}>
                              <div className="bg-white p-4" style={{ minHeight: '600px' }}>
                                <iframe
                                  title="email-preview"
                                  className="w-full border-0"
                                  style={{
                                    minHeight: '600px',
                                    border: 'none',
                                    width: '100%',
                                    display: 'block'
                                  }}
                                  srcDoc={selectedCampaign.htmlContent}
                                  sandbox="allow-same-origin"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <svg className="mx-auto h-12 w-12 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <p>No email content available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Failed to load campaign details
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
              <button
                onClick={closeModal}
                className={`px-4 py-2 rounded-md transition-colors duration-200 ${isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )
      }
    </div >
  );
};

export default AdminCampaignsPage;

